<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import Star from './Star.vue'

// ported from https://github.com/craigh411/vue-star-rating/tree/master
type TStarRatingProps = {
  /**
   * The rating increment, for example pass 0.5 for half stars or 0.01 for fluid stars.
   * Expects a number between 0.01 - 1.
   */
  increment?: number

  /**
   * The label displayed on top of the rating component.
   */
  label?: string

  /**
   * Shows a required asterisk next to the label.
   */
  required?: boolean

  /**
   * The initial rating. This will automatically round to the closest increment.
   * For the most accurate rating, pass 0.01 as increment or set the round-start-rating prop to false.
   */
  rating?: number

  /**
   * Pass false if you don't want the start rating value to round to the closest increment.
   * The user will still only be able to select based on the given increment.
   */
  roundStartRating?: boolean

  /**
   * The color of the highlighted portion of a star.
   * Pass an array of colors to color each star individually.
   */
  activeColor?: string | string[]

  /**
   * The color of the non-highlighted portion of a star.
   */
  inactiveColor?: string

  /**
   * The maximum rating, this lets vue-star-rating know how many stars to display.
   */
  maxRating?: number

  /**
   * The points defining a custom star shape.
   * If no points are passed, the default star shape is used.
   */
  starPoints?: number[]

  /**
   * The size of each star, passed to the SVG width attribute.
   * Larger numbers result in larger stars.
   */
  starSize?: number

  /**
   * Whether or not to show the rating next to the stars.
   */
  showRating?: boolean

  /**
   * When set to true, the rating cannot be edited.
   * Use in conjunction with increment to define rounding precision.
   */
  readOnly?: boolean

  /**
   * A CSS class name to style the rating text for a specific star rating component.
   */
  textClass?: string

  /**
   * Sets the star rating to display inline.
   */
  inline?: boolean

  /**
   * Sets the colour of the border for each star.
   */
  borderColor?: string

  /**
   * The border color of a highlighted star.
   * Pass an array of colors to color each star's border individually.
   */
  activeBorderColor?: string | string[]

  /**
   * Sets the width of the border for each star.
   */
  borderWidth?: number

  /**
   * Whether or not to round the star's corners.
   */
  roundedCorners?: boolean

  /**
   * Pads the right of each star so the distance between stars can be altered.
   */
  padding?: number

  /**
   * Pass true to display star rating using RTL (right-to-left).
   */
  rtl?: boolean

  /**
   * Specify a fixed number of digits after the decimal point.
   */
  fixedPoints?: number | null

  /**
   * Adds a subtle glow around each active star.
   * This should be a number to spread the glow.
   */
  glow?: number

  /**
   * Sets the color for the glow.
   */
  glowColor?: string

  /**
   * When set to true, a second click on the same rating clears the rating.
   */
  clearable?: boolean

  /**
   * When set to true, only apply active colors when the user clicks the star, rather than on mouseover.
   */
  activeOnClick?: boolean

  /**
   * Set to true for an animation to be applied on mouseover.
   */
  animate?: boolean
}

const props = withDefaults(defineProps<TStarRatingProps>(), {
  increment: 1,
  required: false,
  rating: 0,
  roundStartRating: true,
  activeColor: '#ffd055',
  inactiveColor: '#d8d8d8',
  maxRating: 5,
  starPoints: () => [],
  starSize: 30,
  showRating: false,
  readOnly: false,
  textClass: '',
  inline: false,
  borderColor: '#999',
  activeBorderColor: null,
  borderWidth: 0,
  roundedCorners: false,
  padding: 0,
  rtl: false,
  fixedPoints: null,
  glow: 0,
  glowColor: '#fff',
  clearable: false,
  activeOnClick: true,
  animate: true,
})

const emit = defineEmits<{
  (event: 'update:rating', value: number): void
  (event: 'hoverRating', value: number): void
}>()

const step = ref(props.increment * 100)
const fillLevel = ref<number[]>(Array.from({ length: props.maxRating }, () => 0))
const currentRating = ref(props.rating)
const selectedRating = ref(props.rating)
const ratingSelected = ref(false)

const formattedRating = computed(() => {
  return props.fixedPoints === null ? currentRating.value : currentRating.value.toFixed(props.fixedPoints)
})

const currentActiveColor = computed(() => {
  if (Array.isArray(props.activeColor)) {
    const index = Math.min(Math.ceil(currentRating.value) - 1, props.activeColor.length - 1)
    return props.activeColor[index]
  }
  return props.activeColor
})

const roundedRating = computed(() => {
  const inv = 1.0 / props.increment
  return Math.min(props.maxRating, Math.ceil(currentRating.value * inv) / inv)
})

const shouldRound = computed(() => {
  return ratingSelected.value || props.roundStartRating
})

const margin = computed(() => {
  return props.padding + props.borderWidth
})

const currentActiveBorderColor = computed(() => {
  if (Array.isArray(props.activeBorderColor)) {
    const index = Math.min(Math.ceil(currentRating.value) - 1, props.activeBorderColor.length - 1)
    return props.activeBorderColor[index]
  }
  return props.activeBorderColor || props.borderColor
})

const starStyle = computed(() => {
  return { 'margin-right': `${margin.value}px` }
})

watch(() => props.rating, (newVal) => {
  currentRating.value = newVal
  selectedRating.value = newVal
  createStars(shouldRound.value)
})

function setRating(ratingEvent: { id: number; position: number }, persist: boolean) {
  if (!props.readOnly) {
    const position = props.rtl ? (100 - ratingEvent.position) / 100 : ratingEvent.position / 100
    currentRating.value = Math.min(props.maxRating, ((ratingEvent.id + position) - 1).toFixed(2))
    if (persist) {
      createStars(true, true)
      selectedRating.value = props.clearable && currentRating.value === selectedRating.value ? 0 : currentRating.value
      ratingSelected.value = true
      emit('update:rating', selectedRating.value)
    }
    else {
      createStars(true, !props.activeOnClick)
      emit('hoverRating', currentRating.value)
    }
  }
}

function resetRating() {
  if (!props.readOnly) {
    currentRating.value = selectedRating.value
    createStars(shouldRound.value)
  }
}

function createStars(round: boolean, applyFill: boolean = true) {
  currentRating.value = round ? roundedRating.value : currentRating.value
  for (let i = 0; i < props.maxRating; i++) {
    let level = 0
    if (i < currentRating.value)
      level = (currentRating.value - i > 1) ? 100 : (currentRating.value - i) * 100

    if (applyFill)
      fillLevel.value[i] = Math.round(level)
  }
}
createStars(props.roundStartRating)
</script>

<template>
  <div class="flex flex-col" :class="[{ 'vue-star-rating-rtl': rtl }, { 'vue-star-rating-inline': inline }]">
    <div class="sr-only">
      <slot name="screen-reader" :rating="selectedRating" :stars="maxRating">
        <span>Rated {{ selectedRating }} stars out of {{ maxRating }}</span>
      </slot>
    </div>
    <label v-if="label" class="subtitle text-sm">
      <slot name="icon" /> {{ label }}
      <span v-if="required" class="text-danger-400" aria-hidden="true">*</span>
    </label>
    <div class="vue-star-rating" @mouseleave="resetRating">
      <span
        v-for="n in maxRating" :key="n" class="vue-star-rating-star"
        :class="[{ 'vue-star-rating-pointer': !readOnly }]" :style="starStyle"
      >
        <Star
          :fill="fillLevel[n - 1]" :size="starSize" :points="starPoints" :star-id="n" :step="step"
          :active-color="currentActiveColor" :inactive-color="inactiveColor" :border-color="borderColor"
          :active-border-color="currentActiveBorderColor" :border-width="borderWidth" :rounded-corners="roundedCorners"
          :rtl="rtl" :glow="glow" :glow-color="glowColor" :animate="animate" @star-selected="setRating($event, true)"
          @star-mouse-move="setRating($event, false)"
        />
      </span>
      <span v-if="showRating" class="vue-star-rating-rating-text" :class="[textClass]">{{ formattedRating }}</span>
    </div>
  </div>
</template>

<style scoped>
.vue-star-rating-star {
  display: inline-block;
  -webkit-tap-highlight-color: transparent;
}

.vue-star-rating-pointer {
  cursor: pointer;
}

.vue-star-rating-inline {
  display: inline-flex;
}

.vue-star-rating-rating-text {
  margin-left: 7px;
}

.vue-star-rating-rtl {
  direction: rtl;
}

.vue-star-rating-rtl .vue-star-rating-rating-text {
  margin-right: 10px;
  direction: rtl;
}

.sr-only {
  position: absolute;
  left: -10000px;
  top: auto;
  width: 1px;
  height: 1px;
  overflow: hidden;
}
</style>
